/**
 * Reading a module save conflict off a failed save.
 *
 * @module components/studio/module-save-conflict
 */

import type { Module } from "@/types";

/** The stored module a rejected save has to be resolved against. */
export interface ModuleSaveConflict {
  /** The module as stored when the save was refused. */
  currentModule: Module | undefined;
  /** The stored programme-wide PLO-to-MIMLO map. */
  currentPloToMimlos: Record<string, string[]>;
  /** The hash the next save has to be based on. */
  currentModuleHash: string;
}

/**
 * Read the stored module out of a refused save, when that is why it failed.
 *
 * The client branches on the wire `code` rather than the message, and takes
 * the fresh base from `details`. A retry that re-sends the stale base fails
 * identically, leaving the draft recoverable only by navigating away from it.
 *
 * @param error - The value the save rejected with.
 * @returns The conflict, or null when the failure was something else.
 */
export function readModuleConflict(error: unknown): ModuleSaveConflict | null {
  if (!error || typeof error !== "object") {
    return null;
  }
  const { code, details } = error as { code?: unknown; details?: unknown };
  if (code !== "module_revision_conflict" || !details || typeof details !== "object") {
    return null;
  }
  const { currentModule, currentPloToMimlos, currentModuleHash } = details as {
    currentModule?: unknown;
    currentPloToMimlos?: unknown;
    currentModuleHash?: unknown;
  };
  if (typeof currentModuleHash !== "string") {
    return null;
  }
  return {
    currentModule:
      currentModule && typeof currentModule === "object" ? (currentModule as Module) : undefined,
    currentPloToMimlos:
      currentPloToMimlos && typeof currentPloToMimlos === "object"
        ? (currentPloToMimlos as Record<string, string[]>)
        : {},
    currentModuleHash,
  };
}
